import {
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";

import { IAddress, ICountry, IShipco, IShipcoGroup } from "interfaces";
import React from "react";

export const AddressList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IAddress>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<IAddress>({
    mapData: (item) => {
      return {
        "Country": item?.country?.name || "",
        "Shipco": item?.shipco?.registrationName || "",
        "Shipco Group": item?.shipcoGroup?.registrationName || "",
        "Address Line 1": item?.addressLine1 || "",
        "Address Line 2": item?.addressLine2 || "",
        "Address Line 3": item?.addressLine3 || "",
        "City": item?.city || "",
        "State": item?.state || "",
        "Postal Code": item?.postalCode || "",
      };
    },
    unparseConfig: {
      header: true,
      columns: [
        "Country",
        "Shipco",
        "Shipco Group",
        "Address Line 1",
        "Address Line 2",
        "Address Line 3",
        "City",
        "State",
        "Postal Code",
      ],
    },
  });

  const { selectProps: countrySelectProps } = useSelect<ICountry>({
    resource: "country",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: shipcoGroupSelectProps } = useSelect<IShipcoGroup>({
    resource: "shipco-group",
    optionLabel: "registrationName",
    optionValue: "registrationName",

    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IAddress> = [
    {
      title: "Country",
      dataIndex: ["country", "name"],
      key: "country.name",
      width: 150,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Country"
            {...countrySelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Shipco",
      dataIndex: ["shipco", "registrationName"],
      key: "shipco.registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco"
            {...shipcoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Shipco Group",
      dataIndex: ["shipcoGroup", "registrationName"],
      key: "shipcoGroup.registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco Group"
            {...shipcoGroupSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Address Line 1",
      dataIndex: "addressLine1",
      key: "addressLine1",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("addressLine1", sorters),
      sorter: true,
    },
    {
      title: "Address Line 2",
      dataIndex: "addressLine2",
      key: "addressLine2",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("addressLine2", sorters),
      sorter: true,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 140,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("city", sorters),
      sorter: true,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 140,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("state", sorters),
      sorter: true,
    },
    {
      title: "Postal Code",
      dataIndex: "postalCode",
      key: "postalCode",
      width: 120,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("postalCode", sorters),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        subTitle: "active addresses",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
